import { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { FiMic, FiPaperclip, FiPhone, FiPhoneOff, FiSend, FiX } from "react-icons/fi";
import salesService from "../../api/services/salesService";
import { getSupportSocket } from "../../api/services/supportSocket";
import { useVoiceRecorder } from "../../hooks/useVoiceRecorder";
import VoiceNote from "../../components/VoiceNote";
import {
  joinSupportAgoraCall,
  leaveSupportAgoraCall,
  parseSupportAgoraSession,
} from "../../utils/supportAgoraCall";
import {
  startSupportCallRingback,
  stopSupportCallRingback,
  disposeSupportCallRingback,
} from "../../utils/supportCallRingback";
import { ChatSkeleton } from "../../components/common/skeletons";

const THEME = { primary: "#FB565F" };

function formatRecordingTime(seconds) {
  const m = Math.floor(seconds / 60)
    .toString()
    .padStart(2, "0");
  const s = (seconds % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

export default function SalesCustomerChat({ customer, onClose }) {
  const [messages, setMessages] = useState([]);
  const [chatId, setChatId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [startingCall, setStartingCall] = useState(false);
  const [activeCall, setActiveCall] = useState(null);
  const [callPhase, setCallPhase] = useState("idle");
  const callClientRef = useRef(null);
  const callStartRef = useRef(0);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const userId = customer?.userId;

  const loadChat = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const contact = await salesService.contactCustomer(userId);
      setChatId(contact.chatId);
      const data = await salesService.getChatMessages(userId);
      setMessages(data.messages || []);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to open chat");
      onClose?.();
    } finally {
      setLoading(false);
    }
  }, [userId, onClose]);

  useEffect(() => {
    loadChat();
  }, [loadChat]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (!chatId) return undefined;
    const socket = getSupportSocket();
    const onMessage = (msg) => {
      const msgChatId = Number(msg.chat_id || msg.chatId);
      if (msgChatId !== Number(chatId)) return;
      setMessages((prev) => {
        const id = msg.id || msg.messageId;
        if (id && prev.some((m) => m.id === id)) return prev;
        return [...prev, msg];
      });
    };
    socket.on("message", onMessage);
    return () => socket.off("message", onMessage);
  }, [chatId]);

  useEffect(
    () => () => {
      disposeSupportCallRingback();
      leaveSupportAgoraCall(callClientRef.current, callClientRef.current?.localAudioTrack);
    },
    []
  );

  const resetCallUi = () => {
    stopSupportCallRingback();
    setCallPhase("idle");
    callStartRef.current = 0;
  };

  const handleCustomerJoinedCall = () => {
    stopSupportCallRingback();
    setCallPhase("active");
    callStartRef.current = Date.now();
  };

  const sendMessage = async () => {
    if (!text.trim() || sending) return;
    const payload = text.trim();
    setSending(true);
    setText("");
    try {
      const res = await salesService.sendChatMessage(userId, payload);
      if (res.message) {
        setMessages((prev) =>
          prev.some((m) => m.id === res.message.id) ? prev : [...prev, res.message]
        );
      }
    } catch (e) {
      setText(payload);
      toast.error(e?.response?.data?.message || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  const sendVoiceMessage = useCallback(
    async (blob) => {
      setSending(true);
      try {
        const res = await salesService.sendChatVoice(userId, blob);
        if (res.message) {
          setMessages((prev) =>
            prev.some((m) => m.id === res.message.id) ? prev : [...prev, res.message]
          );
        }
      } catch (e) {
        toast.error(e?.response?.data?.message || "Failed to send voice");
      } finally {
        setSending(false);
      }
    },
    [userId]
  );

  const sendImageAttachment = async (file) => {
    if (!file || sending) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file (JPG, PNG, GIF, WebP)");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image must be under 10 MB");
      return;
    }
    setSending(true);
    try {
      const res = await salesService.sendChatImage(userId, file);
      if (res.message) {
        setMessages((prev) =>
          prev.some((m) => m.id === res.message.id) ? prev : [...prev, res.message]
        );
      }
      toast.success("Image sent");
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.response?.data?.error || "Failed to send image");
    } finally {
      setSending(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleAttachmentPick = (e) => {
    const file = e.target.files?.[0];
    if (file) sendImageAttachment(file);
  };

  const { recording, recordingTime, startRecording, stopRecording, cancelRecording } =
    useVoiceRecorder(sendVoiceMessage, {
      onRecordingTooShort: () => toast.error("Recording too short"),
    });

  const startCall = async () => {
    if (startingCall || activeCall) return;
    setStartingCall(true);
    try {
      const res = await salesService.initiateCall(userId, "voice");
      const session = parseSupportAgoraSession(res);
      const { client, localAudioTrack } = await joinSupportAgoraCall(session, {
        onRemoteUserJoined: handleCustomerJoinedCall,
      });
      callClientRef.current = client;
      callClientRef.current.localAudioTrack = localAudioTrack;
      setActiveCall(res);
      setCallPhase("ringing");
      callStartRef.current = 0;
      await startSupportCallRingback();
    } catch (e) {
      await leaveSupportAgoraCall(callClientRef.current, callClientRef.current?.localAudioTrack);
      callClientRef.current = null;
      resetCallUi();
      setActiveCall(null);
      toast.error(e?.response?.data?.message || e?.message || "Failed to start call");
    } finally {
      setStartingCall(false);
    }
  };

  const endCall = async () => {
    if (!activeCall) return;
    const duration = callStartRef.current
      ? Math.floor((Date.now() - callStartRef.current) / 1000)
      : 0;
    try {
      await salesService.endCall(userId, {
        channelName: activeCall.channelName,
        durationSeconds: duration,
        callId: activeCall.callId,
      });
      await leaveSupportAgoraCall(callClientRef.current, callClientRef.current?.localAudioTrack);
    } catch {
      /* ignore */
    }
    callClientRef.current = null;
    setActiveCall(null);
    resetCallUi();
    loadChat();
  };

  const renderMessage = (m) => {
    const isAgent = m.isAgent || m.user_type === "AGENT" || m.user_type === "SENDER";
    return (
      <div key={m.id} className={`flex mb-3 ${isAgent ? "justify-end" : "justify-start"}`}>
        <div
          className={`max-w-[78%] rounded-2xl px-4 py-2.5 text-sm shadow-sm ${
            isAgent ? "text-white rounded-tr-sm" : "bg-gray-100 text-gray-800 rounded-tl-sm border border-gray-200"
          }`}
          style={isAgent ? { backgroundColor: THEME.primary } : {}}
        >
          {(m.type === "TEXT" || (!m.type && m.message && m.type !== "IMAGE")) && (
            <p className="whitespace-pre-wrap break-words">{m.message}</p>
          )}
          {m.type === "IMAGE" && m.image && (
            <a href={m.image} target="_blank" rel="noopener noreferrer">
              <img
                src={m.image}
                alt="Attachment"
                className="max-h-44 rounded-lg border border-white/20 object-cover"
              />
            </a>
          )}
          {m.type === "VOICE" && m.voice && (
            <div className="inline-block max-w-full min-w-[12rem]">
              <VoiceNote audioUrl={m.voice} />
            </div>
          )}
          {m.type === "CALL" && (
            <p className="flex items-center gap-1.5">
              <FiPhone className="shrink-0" />
              {m.call_type || "voice"} call
              {m.call_duration_seconds
                ? ` (${Math.floor(m.call_duration_seconds / 60)}:${String(
                    m.call_duration_seconds % 60
                  ).padStart(2, "0")})`
                : ""}
            </p>
          )}
        </div>
      </div>
    );
  };

  const name = `${customer?.firstName || ""} ${customer?.lastName || ""}`.trim();

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <div>
            <h3 className="font-bold text-gray-900">{name || "Customer"}</h3>
            <p className="text-xs text-gray-400">{customer?.email}</p>
          </div>
          <div className="flex items-center gap-2">
            {activeCall ? (
              <button
                type="button"
                onClick={endCall}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-600 text-white"
              >
                <FiPhoneOff /> End call
              </button>
            ) : (
              <button
                type="button"
                onClick={startCall}
                disabled={startingCall}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-emerald-600 text-white disabled:opacity-50"
              >
                <FiPhone /> Call
              </button>
            )}
            <button type="button" onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100">
              <FiX />
            </button>
          </div>
        </div>

        {callPhase === "ringing" && (
          <p className="text-center text-xs text-amber-700 bg-amber-50 py-2">Ringing customer…</p>
        )}
        {callPhase === "active" && (
          <p className="text-center text-xs text-emerald-700 bg-emerald-50 py-2">Call in progress</p>
        )}

        <div className="flex-1 overflow-y-auto p-4 min-h-[280px]">
          {loading ? (
            <ChatSkeleton rows={8} />
          ) : (
            <>
              {messages.map(renderMessage)}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        <div className="border-t border-gray-100 p-3">
          {recording ? (
            <div className="flex items-center gap-2">
              <span className="text-red-600 text-sm font-semibold animate-pulse">
                {formatRecordingTime(recordingTime)}
              </span>
              <button
                type="button"
                onClick={stopRecording}
                className="px-3 py-2 rounded-lg text-sm font-semibold text-white"
                style={{ backgroundColor: THEME.primary }}
              >
                Send voice
              </button>
              <button type="button" onClick={cancelRecording} className="px-3 py-2 text-sm text-gray-500">
                Cancel
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                className="hidden"
                onChange={handleAttachmentPick}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={sending || !!activeCall}
                className="p-2 rounded-lg bg-gray-100 text-gray-600 disabled:opacity-50"
                title="Attach image"
              >
                <FiPaperclip />
              </button>
              <button
                type="button"
                onClick={startRecording}
                disabled={sending || !!activeCall}
                className="p-2 rounded-lg bg-gray-100 text-gray-600 disabled:opacity-50"
                title="Voice message"
              >
                <FiMic />
              </button>
              <input
                type="text"
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && sendMessage()}
                placeholder="Type a message…"
                className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-red-300"
                disabled={sending || !!activeCall}
              />
              <button
                type="button"
                onClick={sendMessage}
                disabled={!text.trim() || sending}
                className="p-2 rounded-lg text-white disabled:opacity-50"
                style={{ backgroundColor: THEME.primary }}
              >
                <FiSend />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
